import { clearChatOpenPayload, loadMoreMessages } from 'app-components/flux/llmInterface';

import * as llmSelectors from 'app-components/flux/llmInterface/llmSelectors';

import { getLogger } from 'app-components/utils/core';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { makeStyles } from 'tss-react/mui';
import { randomGreeting } from './utils';
import ChatFeedback from './ChatFeedback';
import ChatDefaultState from './ChatDefaultState';
import { createMarkdownComponents } from 'utils/createMarkdownComponents';

import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { profileSelectors } from 'app-components/flux/profile';
import { AIAvatar } from 'components/AIComponents/AIAvatar';
import { useChatSession } from 'components/ChatLauncher/chatSession';
import ChartPreviewDialog from 'components/Dialogs/ChartPreviewDialog';
// import { useVoiceRecorder } from 'hooks/useVoiceRecorder';
import ChatControls from './ChatControls';
import ChatHeader from './ChatHeader';
import ChatSideBar from './ChatSideBar';
import ChartPreview from './chartPreviews/ChartPreview';
import type { WebChartRequest } from './llmWebTools';

const log = getLogger('SimpleChat');

const TOP_SCROLL_THRESHOLD = 80;
const BOTTOM_SCROLL_THRESHOLD = 100;

const useStyles = makeStyles<{ isDocked: boolean }>({ name: 'AI-Chat' })((theme, { isDocked }) => ({
  dialogBorder: {
    background: theme.palette.ai.background,
    borderRadius: isDocked ? 0 : 12,
    width: isDocked ? '100%' : 500,
    height: isDocked ? '100%' : '80vh',
    padding: isDocked ? 0 : 8,
    backdropFilter: 'blur(4px)',
    zIndex: theme.zIndex.modal - 1,
    transition: 'all 0.3s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: isDocked ? theme.shadows[0] : theme.shadows[2],
    ...(isDocked
      ? {
          position: 'relative',
        }
      : {
          right: theme.spacing(2),
        }),
  },
  paper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.light} 100%)`,
    backdropFilter: 'blur(16px)',
    overflow: 'hidden',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  messageList: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(14),
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    flexGrow: 1,
    overflowY: 'auto',
    borderRadius: theme.shape.borderRadius,
    userSelect: 'text',
  },
  messageBoxUser: {
    display: 'inherit',
    textAlign: 'right',
    margin: theme.spacing(1.5, 0),
  },
  messageBoxAi: {
    display: 'flex',
    textAlign: 'left',
    margin: theme.spacing(1.5, 0),
  },
  messageBubbleUser: {
    display: 'inline-block',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.primary.light,
    borderRadius: '12px 4px 12px 12px',
    marginLeft: theme.spacing(5),
  },
  messageBubbleAi: {
    display: 'inline-block',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius * 2,
  },
  chartBubble: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius * 2,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
  },
  chartHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  chartTitle: {
    flex: 1,
    minWidth: 0,
  },
  streamingMessageBox: {
    textAlign: 'left',
    marginBottom: theme.spacing(1),
    display: 'flex',
  },
  spinnerBubble: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: theme.spacing(6),
    minHeight: theme.spacing(4),
  },
  messageBubbleReasoning: {
    padding: theme.spacing(0, 1),
    color: theme.palette.text.tertiary,
    borderRadius: theme.shape.borderRadius * 2,
  },
  paginationRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    color: theme.palette.text.secondary,
  },
  paginationError: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
  scrollToBottom: {
    position: 'absolute',
    left: '50%',
    bottom: theme.spacing(12),
    transform: 'translateX(-50%)',
    zIndex: 2,
  },
  scrollToBottomButton: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    boxShadow: theme.shadows[2],
  },
}));

type SimpleChatProps = {
  handleMinimize: () => void;
  handleDock: () => void;
  isDocked: boolean;
  onClose: () => void;
  dragHandleRef?: React.RefObject<HTMLDivElement>;
  open?: boolean;
  isMinimized?: boolean;
  greeting?: string;
};

const AICHat = ({
  handleMinimize,
  handleDock,
  isDocked,
  onClose,
  dragHandleRef,
  open: _open,
  isMinimized: _isMinimized,
  greeting: greetingProp,
}: SimpleChatProps): JSX.Element => {
  const [input, setInput] = React.useState('');
  const [showSideBarMenu, setShowSideBarMenu] = React.useState(false);
  const [paginationError, setPaginationError] = React.useState<string | null>(null);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = React.useState(true);
  type ChartWithCallId = WebChartRequest & { callId: string };
  const [chartsByCallId, setChartsByCallId] = React.useState<Record<string, ChartWithCallId>>({});
  const [chartFailuresByCallId, setChartFailuresByCallId] = React.useState<Record<string, boolean>>({});
  const [activeChartCallId, setActiveChartCallId] = React.useState<string | null>(null);
  const { classes, theme } = useStyles({ isDocked });

  const dispatch = useAppDispatch();
  const { firstName = '' } = useAppSelector((state) => profileSelectors.getProfile(state)) || {};
  const chartsEnabled = useAppSelector((state) =>
    llmSelectors.getPlatformSpecificTools(state).some((tool) => tool.name === 'web_build_chart'),
  );
  const fallbackGreeting = useMemo(() => randomGreeting(), []);
  const greeting = greetingProp ?? fallbackGreeting;

  const { conversationId } = useChatSession();
  const isLoadingConversations = useAppSelector(llmSelectors.isLoadingConversations);
  const hasMore = useAppSelector((state) =>
    conversationId ? llmSelectors.selectConversationHasMore(state, conversationId) : false,
  );
  const isLoadingMore = useAppSelector((state) =>
    conversationId ? llmSelectors.selectConversationIsLoadingMore(state, conversationId) : false,
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const prevScrollHeightRef = useRef<number | null>(null);
  const isInitialLoadRef = useRef(true);
  const autoScrollEnabledRef = useRef(true);

  const {
    messages,
    streamingMessage,
    reasoningMessage,
    sendMessage,
    isReady,
    error,
    prepareConversation,
    startNewConversation,
  } = useChatSession();
  const chatOpenPayload = useAppSelector(llmSelectors.getChatOpenPayload);
  const [pendingAutoSendMessage, setPendingAutoSendMessage] = React.useState<string | null>(null);

  const isStreaming = useMemo(() => streamingMessage.length > 0, [streamingMessage]);
  const localDragHandleRef = useRef<HTMLDivElement | null>(null);
  const handleRef = dragHandleRef ?? localDragHandleRef;
  const isLoading = (isLoadingMore || isLoadingConversations) && !streamingMessage;
  const isPaginating = isLoadingMore && prevScrollHeightRef.current != null;
  const lastMessage = messages[messages.length - 1];
  const isWaitingForResponse = messages.length > 0 && lastMessage?.role === 'user';
  const showStatus = (isWaitingForResponse || reasoningMessage) && !streamingMessage;

  // Default state: when conversation hasn't started (no messages, not streaming, not loading, not showing status)
  const chartCount = Object.keys(chartsByCallId).length;
  const activeChart = activeChartCallId ? chartsByCallId[activeChartCallId] : null;
  const isDefaultState = useMemo(
    () =>
      messages.length === 0 &&
      chartCount === 0 &&
      !streamingMessage &&
      !isLoading &&
      !showStatus &&
      !isLoadingConversations,
    [messages.length, chartCount, streamingMessage, isLoading, showStatus, isLoadingConversations],
  );
  const showScrollToBottom = !isDefaultState && !isAutoScrollEnabled;

  const markdownComponents = useMemo(() => createMarkdownComponents(theme), [theme]);

  useEffect(() => {
    if (!chatOpenPayload) return;
    const { conversationMode } = chatOpenPayload;
    const shouldStartNew = conversationMode === 'new';
    const shouldPrepareExisting = conversationMode === 'existing' || (!conversationMode && chatOpenPayload.autoSend);
    const message = chatOpenPayload.message?.trim();

    if (shouldStartNew) {
      startNewConversation().catch((err: unknown) => {
        log.error('AIChat: Failed to start new conversation for open payload', err);
      });
    } else if (shouldPrepareExisting) {
      prepareConversation().catch((err: unknown) => {
        log.error('AIChat: Failed to prepare conversation for open payload', err);
      });
    }

    if (message) {
      if (chatOpenPayload.autoSend) {
        setPendingAutoSendMessage(message);
      } else if (!input.trim()) {
        setInput(message);
      }
    }
    dispatch(clearChatOpenPayload());
  }, [chatOpenPayload, dispatch, input, prepareConversation, startNewConversation]);

  useEffect(() => {
    if (!pendingAutoSendMessage || !isReady) return;
    sendMessage(pendingAutoSendMessage).catch((err: unknown) => {
      log.error('AIChat: Failed to send open payload message', err);
    });
    setPendingAutoSendMessage(null);
  }, [pendingAutoSendMessage, isReady, sendMessage]);

  // Voice recording disabled for now
  // const { recording, startRecording, stopRecording } = useVoiceRecorder({
  //   send: (buf) => llmSocketInterface.send(buf), // WebSocket, WebRTC, MediaSource, whatever
  //   isReady: true,
  //   addLog: (m) => log.debug(m),
  //   sampleRate: 16000,
  //   bufferSize: 4096,
  // });

  useEffect(() => {
    if (!chartsEnabled) {
      if (
        Object.keys(chartsByCallId).length > 0 ||
        Object.keys(chartFailuresByCallId).length > 0 ||
        activeChartCallId !== null
      ) {
        setChartsByCallId({});
        setChartFailuresByCallId({});
        setActiveChartCallId(null);
      }
      return;
    }

    for (const msg of messages) {
      if (msg.type !== 'function_call') continue;
      if (msg.content?.name !== 'web_build_chart') continue;

      const callId = msg.content?.callId;
      if (!callId) continue;

      // Already processed
      if (chartsByCallId[callId] || chartFailuresByCallId[callId]) {
        continue;
      }

      try {
        const args =
          typeof msg.content.arguments === 'string' ? JSON.parse(msg.content.arguments) : msg.content.arguments;

        if (!args) {
          setChartFailuresByCallId((prev) => ({ ...prev, [callId]: true }));
          continue;
        }

        setChartsByCallId((prev) => ({
          ...prev,
          [callId]: {
            ...args,
            callId,
          },
        }));
      } catch (err) {
        log.error('Failed to parse web_build_chart args', err);
        setChartFailuresByCallId((prev) => ({ ...prev, [callId]: true }));
      }
    }
  }, [messages, chartsByCallId, chartFailuresByCallId, activeChartCallId, chartsEnabled]);

  const handleSubmit = (e: React.FormEvent, message?: string) => {
    e.preventDefault();
    const currentInput = message ?? input;
    if (!currentInput.trim()) return;
    if (!isReady || isLoading) return;
    sendMessage(currentInput).catch((err: unknown) => {
      log.error('AIChat: Failed to send message', err);
    });
    setInput('');
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    // Don’t fight the "load older messages" logic
    if (prevScrollHeightRef.current != null) return;
    if (!autoScrollEnabledRef.current) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, streamingMessage]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    // First load: just jump to bottom
    if (isInitialLoadRef.current) {
      container.scrollTop = container.scrollHeight;
      isInitialLoadRef.current = false;
      return;
    }

    // After loading older messages: keep viewport anchored
    if (prevScrollHeightRef.current != null && !isLoadingMore) {
      const diff = container.scrollHeight - prevScrollHeightRef.current;
      container.scrollTop += diff;
      prevScrollHeightRef.current = null;
    }
  }, [messages.length, isLoadingMore]);

  const loadOlderMessages = useCallback((): void => {
    if (!hasMore || isLoadingMore || !conversationId) return;

    const container = scrollRef.current;
    if (container) {
      // capture height *before* we prepend
      prevScrollHeightRef.current = container.scrollHeight;
    }

    setPaginationError(null);

    dispatch(loadMoreMessages(conversationId))
      .unwrap()
      .catch((err: unknown) => {
        if (typeof err === 'string' && err === 'No more items to load') {
          return;
        }
        log.error('Failed to load older messages', err);
        setPaginationError('Unable to load older messages.');
      });
  }, [dispatch, hasMore, isLoadingMore, conversationId]);

  const setAutoScrollEnabled = useCallback((enabled: boolean) => {
    if (autoScrollEnabledRef.current === enabled) return;
    autoScrollEnabledRef.current = enabled;
    setIsAutoScrollEnabled(enabled);
  }, []);

  const updateAutoScrollFromUser = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const isNearBottom = distanceFromBottom <= BOTTOM_SCROLL_THRESHOLD;
    setAutoScrollEnabled(isNearBottom);
  }, [setAutoScrollEnabled]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const container = scrollRef.current;
      if (!container) return;

      if (event.nativeEvent.isTrusted) {
        updateAutoScrollFromUser();
      }

      if (container.scrollTop <= TOP_SCROLL_THRESHOLD) {
        loadOlderMessages();
      }
    },
    [loadOlderMessages, updateAutoScrollFromUser],
  );

  const handleScrollToBottom = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    setAutoScrollEnabled(true);
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [setAutoScrollEnabled]);

  const handleOpenChartDialog = (chart: ChartWithCallId) => {
    setActiveChartCallId(chart.callId);
  };

  const handleCloseChartDialog = () => {
    setActiveChartCallId(null);
  };

  return (
    <Box className={classes.dialogBorder}>
      <Paper id='simple-chat-interface' elevation={3} className={classes.paper}>
        <ChatHeader
          onClose={onClose}
          setShowSideBarMenu={setShowSideBarMenu}
          handleMinimize={handleMinimize}
          handleDock={handleDock}
          isDocked={isDocked}
          handleRef={handleRef}
          isDefaultState={isDefaultState}
        />
        {showSideBarMenu && <ChatSideBar open={!!showSideBarMenu} onClose={() => setShowSideBarMenu(false)} />}

        <Box ref={scrollRef} onScroll={handleScroll} className={classes.messageList}>
          {isPaginating && (
            <Box className={classes.paginationRow} role='status' aria-live='polite'>
              <CircularProgress size={16} color='inherit' />
              <Typography variant='captionRegular'>Loading older messages...</Typography>
            </Box>
          )}
          {paginationError && !isLoadingMore && (
            <Box className={classes.paginationError} role='alert'>
              <Typography variant='captionRegular' color='error'>
                {paginationError}
              </Typography>
              <Button size='small' variant='text' color='error' onClick={loadOlderMessages}>
                Retry
              </Button>
            </Box>
          )}
          {isDefaultState && (
            <ChatDefaultState
              firstName={firstName}
              greeting={greeting}
              handleSubmit={handleSubmit}
              input={input}
              isLoading={isLoading}
              isStreaming={isStreaming}
              isReady={isReady}
              setInput={setInput}
            />
          )}
          {messages

            .filter(
              (m) =>
                (m.role === 'user' || m.role === 'assistant') &&
                (m.type === 'message' || m.type === 'error' || (chartsEnabled && m.type === 'function_call')),
            )
            .map((msg, i) => {
              const messageKey = msg.id ?? msg.content?.callId ?? i;
              const isUser = msg.role === 'user';

              if (msg.type === 'function_call') {
                const toolName = msg.content?.name;
                if (toolName !== 'web_build_chart') {
                  return null;
                }

                const toolCallId = msg.content?.callId;
                const chart = toolCallId ? chartsByCallId[toolCallId] : undefined;
                const chartFailed = toolCallId ? chartFailuresByCallId[toolCallId] : true;

                return (
                  <React.Fragment key={messageKey}>
                    <Box className={classes.messageBoxAi}>
                      <Box paddingRight={1} paddingTop={2} sx={{ zIndex: 0 }}>
                        <Box sx={{ position: 'relative', zIndex: 0 }}>
                          <AIAvatar />
                        </Box>
                      </Box>
                      <Box className={classes.chartBubble}>
                        <Box className={classes.chartHeader}>
                          <Typography
                            variant='headlineMedium'
                            gutterBottom
                            noWrap
                            title={chart?.title || 'Chart preview'}
                            className={classes.chartTitle}
                          >
                            {chart?.title || 'Chart preview'}
                          </Typography>
                          {chart && (
                            <IconButton
                              size='small'
                              aria-label='Open chart'
                              onClick={() => handleOpenChartDialog(chart)}
                            >
                              <LaunchOutlinedIcon fontSize='small' />
                            </IconButton>
                          )}
                        </Box>
                        {chart?.description && (
                          <Typography variant='captionRegular' color='textSecondary'>
                            {chart.description}
                          </Typography>
                        )}
                        <Box sx={{ marginTop: theme.spacing(1) }}>
                          {chart ? (
                            <ChartPreview chart={chart} />
                          ) : chartFailed ? (
                            <Typography variant='captionRegular' color='textSecondary'>
                              Chart preview unavailable.
                            </Typography>
                          ) : (
                            <Box display='flex' alignItems='center' gap={1}>
                              <CircularProgress size={16} />
                              <Typography variant='captionRegular' color='textSecondary'>
                                Preparing chart...
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </React.Fragment>
                );
              }

              const { text } = msg.content ?? '';
              // Disabled relatedQuestions and legal disclaimer for now
              // const relatedQuestions = metadata?.relatedQuestions ?? [];
              // const customLegalDisclaimer = metadata?.customLegalDisclaimer ?? false;

              return (
                <React.Fragment key={messageKey}>
                  <Box className={isUser ? classes.messageBoxUser : classes.messageBoxAi}>
                    {!isUser && (
                      <Box paddingRight={1} paddingTop={2} sx={{ zIndex: 0 }}>
                        <Box sx={{ position: 'relative', zIndex: 0 }}>
                          <AIAvatar />
                        </Box>
                      </Box>
                    )}
                    <Typography
                      component='div'
                      variant='paragraphRegular'
                      className={isUser ? classes.messageBubbleUser : classes.messageBubbleAi}
                    >
                      {msg.type === 'error' ? (
                        <Typography sx={{ color: theme.palette.error.main }}>{text}</Typography>
                      ) : (
                        <ReactMarkdown
                          remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
                          rehypePlugins={[rehypeRaw]}
                          components={markdownComponents}
                        >
                          {text}
                        </ReactMarkdown>
                      )}
                    </Typography>
                  </Box>
                  {!isUser && msg.type === 'message' && (
                    <Box sx={{ marginLeft: theme.spacing(5) }}>
                      <ChatFeedback messageId={messageKey} aiResponse={text || ''} />
                    </Box>
                  )}
                  {/* <Box>
                  {customLegalDisclaimer && <Typography>Legal Disclaimer</Typography>}
                  {relatedQuestions?.map((question: string, i: number) => (
                    <Chip
                      sx={{ margin: 0.5 }}
                      variant='outlined'
                      label={question}
                      key={`${question}-${i}`}
                      onClick={() => askRelatedQuestion(question)}
                    />
                  ))}
                </Box> */}
                </React.Fragment>
              );
            })}

          {showStatus && (
            <Box className={classes.streamingMessageBox}>
              <AIAvatar />
              <Typography
                component='div'
                variant='paragraphRegular'
                className={`${classes.messageBubbleReasoning} ${classes.spinnerBubble}`}
              >
                {reasoningMessage ? (
                  <Box display='flex' alignItems='center' gap={1}>
                    {reasoningMessage}
                  </Box>
                ) : (
                  <CircularProgress size={16} color='inherit' />
                )}
              </Typography>
            </Box>
          )}

          {streamingMessage && (
            <Box className={classes.streamingMessageBox}>
              <AIAvatar />
              <Typography component='div' variant='paragraphRegular' className={classes.messageBubbleAi}>
                <ReactMarkdown
                  remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
                  rehypePlugins={[rehypeRaw]}
                  components={markdownComponents}
                >
                  {streamingMessage}
                </ReactMarkdown>
              </Typography>
            </Box>
          )}

          {error && (
            <Box className={classes.messageBoxAi}>
              <Typography color='error'>{error}</Typography>
            </Box>
          )}
        </Box>
        {activeChart && <ChartPreviewDialog open onClose={handleCloseChartDialog} chart={activeChart} />}
        {!isDefaultState && (
          <>
            {showScrollToBottom && (
              <Box className={classes.scrollToBottom}>
                <IconButton
                  className={classes.scrollToBottomButton}
                  size='small'
                  aria-label='Scroll to bottom'
                  onClick={handleScrollToBottom}
                >
                  <ArrowDownwardRoundedIcon fontSize='small' />
                </IconButton>
              </Box>
            )}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: theme.spacing(12),
                background: `linear-gradient(to top, ${theme.palette.background.paper} 50%, transparent)`,
                zIndex: 0,
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            />
            <ChatControls
              handleSubmit={handleSubmit}
              // recording={recording}
              // startRecording={startRecording}
              // stopRecording={stopRecording}
              input={input}
              isLoading={isLoading}
              isStreaming={isStreaming}
              isReady={isReady}
              setInput={setInput}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AICHat;
